import { useSliderContext } from 'components/well-page-content/subsurface-explorer/slider-context';
import { EvalPointMdSlider } from './eval-point-md-slider';
import { GunBarrelAzimuthSlider } from './gun-barrel-azimuth-slider';

/**
 * Renders the spacing subject's reference control, reading it from slider
 * context. Outside a `SliderContextProvider` this renders nothing — the
 * well-3d-viewer modal mounts `ControlsPanel` without slider state and shows no
 * slider, which is the pre-existing behaviour.
 *
 * Which control appears depends on the SUBJECT's geometry, not on what happens
 * to be inside the search radius: a horizontal subject has a lateral to slide a
 * reference point along, whereas a vertical has none and instead needs a
 * cross-section bearing (its spacing is evaluated at TD along that bearing).
 * The two are mutually exclusive — a vertical has no MD to offer, and a
 * horizontal derives its azimuth from the wellbore — so exactly one renders.
 *
 * Lives in its own module so it can be tested against a plain context provider
 * without pulling in well-3d-viewer's Mapbox and 3D-scene dependencies.
 *
 * @return {JSX.Element | null} The azimuth slider for a vertical subject, the MD
 *   slider for any other subject, or null outside slider context.
 */
export const SubjectReferenceSliderFromContext = () => {
  const slider = useSliderContext();
  if (!slider) return null;

  if (slider.subjectIsVertical) {
    return (
      <GunBarrelAzimuthSlider
        azimuthDeg={slider.gunBarrelAzimuthDeg}
        onChange={slider.onGunBarrelAzimuthChange}
      />
    );
  }

  // Adapter: the slider's onChange is (md: number) => void while the context
  // exposes (md: number | null) => void. The slider only ever emits numbers.
  const handleChange = (md: number) => slider.onEvalPointMdChange(md);
  return (
    <EvalPointMdSlider
      evalPoints={slider.evalPoints}
      selectedMd={slider.selectedEvalPointMd}
      onChange={handleChange}
    />
  );
};
